import React from 'react';
import { Check, Users, Star, Zap, Sparkles, Clock, PartyPopper, Shirt, Gamepad2, Crown } from 'lucide-react';
import { Button } from './ui/button';

const setGlow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
  e.currentTarget.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
};

const IncludeItem = ({ title, sub, accent, bg, border }) => (
  <li className="flex items-start gap-3">
    <div
      className="flex items-center justify-center w-7 h-7 rounded-md shrink-0 mt-0.5"
      style={{ background: bg, border: `1px solid ${border}` }}
    >
      <Check size={14} style={{ color: accent }} strokeWidth={3} />
    </div>
    <div>
      <p className="text-white font-bold text-sm md:text-base">{title}</p>
      {sub && <p className="text-secondary text-xs md:text-sm">{sub}</p>}
    </div>
  </li>
);

const MetaChip = ({ icon, label, color }) => (
  <div
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider text-white"
    style={{ borderColor: `${color}59`, background: `${color}14` }}
  >
    {icon}
    {label}
  </div>
);

export const PartyPackagesSection = ({ onBookNow }) => (
  <section className="py-16 bg-card relative overflow-hidden" data-testid="party-packages-section">
    <div className="absolute inset-0 cyber-grid opacity-20"></div>
    <div className="container mx-auto px-6 relative z-10">
      <div className="text-center mb-10">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 bg-page border-2 rounded-full mb-4"
          style={{ borderColor: 'var(--neon-teal)', boxShadow: '0 0 15px rgba(0, 255, 204, 0.45)' }}
        >
          <PartyPopper size={20} style={{ color: 'var(--neon-teal)' }} />
          <span className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: 'var(--neon-teal)' }}>
            Birthday Party Packages
          </span>
        </div>
        <h2 className="heading-2 text-4xl md:text-5xl font-black uppercase leading-tight mb-3">
          <span style={{ color: 'var(--neon-teal)', textShadow: '0 0 20px rgba(0, 255, 204, 0.7)' }}>Br8k Room </span>
          <span className="text-pink" style={{ textShadow: '0 0 20px rgba(255, 0, 255, 0.6)' }}>Party Packages</span>
        </h2>
        <p className="body-large text-white max-w-3xl mx-auto">
          Pick your mode. We handle the rest — VR adventures, retro gaming, and a celebration they'll never forget.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* ROOKIE MODE */}
        <div
          data-testid="rookie-mode-card"
          onMouseMove={setGlow}
          className="tier-card relative bg-page border-2 rounded-2xl p-7 md:p-8 flex flex-col"
          style={{
            borderColor: 'var(--neon-teal)',
            '--tier-shadow-base': '0 0 22px rgba(0, 255, 204, 0.4), 0 0 45px rgba(0, 255, 204, 0.12)',
            '--tier-shadow-hover': '0 0 45px rgba(0, 255, 204, 0.85), 0 0 90px rgba(0, 255, 204, 0.35)',
            '--tier-accent': 'rgba(0, 255, 204, 0.6)',
            '--tier-solid': 'var(--neon-teal)',
            '--tier-glow-ring': 'rgba(0, 255, 204, 0.7)',
            '--tier-glow-halo': 'rgba(0, 255, 204, 0.35)',
          }}
        >
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
            <span
              className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[11px] font-black uppercase tracking-wider whitespace-nowrap border-2"
              style={{
                background: 'var(--neon-teal)',
                color: '#0a0e27',
                borderColor: 'var(--bg-page, #0a0e27)',
                boxShadow: '0 0 14px rgba(0, 255, 204, 0.75)',
              }}
            >
              <Gamepad2 size={12} />
              Start The Adventure
            </span>
          </div>

          <div className="relative z-10 text-center mb-5 pb-5 border-b border-muted/50">
            <h3 className="text-3xl md:text-4xl font-black uppercase mb-3" style={{ color: 'var(--neon-teal)', textShadow: '0 0 18px rgba(0, 255, 204, 0.55)' }}>
              Rookie Mode
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <MetaChip icon={<Users size={13} style={{ color: 'var(--neon-teal)' }} />} label="Up to 6 Guests" color="#00ffcc" />
              <MetaChip icon={<Clock size={13} style={{ color: 'var(--neon-teal)' }} />} label="~2 Hours" color="#00ffcc" />
              <MetaChip icon={<Star size={13} style={{ color: 'var(--neon-teal)' }} />} label="Ages 7+" color="#00ffcc" />
            </div>
            <p className="text-secondary text-xs mt-3">Add up to 2 additional guests</p>
          </div>

          <p className="relative z-10 text-white/90 text-sm md:text-base leading-relaxed mb-6">
            The perfect introduction to The Br8k Room! Rookie Mode combines immersive VR gameplay, retro gaming, and exclusive party space — built for birthdays, friends, and first-time adventurers.
          </p>

          <div className="relative z-10 mb-5">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--neon-teal)' }}>
              Package Includes
            </p>
            <ul className="space-y-3">
              <IncludeItem title="Private VR Adventure" sub="30 minutes" accent="var(--neon-teal)" bg="rgba(0, 255, 204, 0.15)" border="rgba(0, 255, 204, 0.45)" />
              <IncludeItem title="60 Minutes of Retro Gaming" sub="40,000+ games across 3 consoles" accent="var(--neon-teal)" bg="rgba(0, 255, 204, 0.15)" border="rgba(0, 255, 204, 0.45)" />
              <IncludeItem title="Reserved Celebration Table Area" sub="Dedicated space for cake, gifts, and guests" accent="var(--neon-teal)" bg="rgba(0, 255, 204, 0.15)" border="rgba(0, 255, 204, 0.45)" />
              <IncludeItem title="Lounge Area with TV & Nex Playground Access" sub="Chill zone for breaks between games" accent="var(--neon-teal)" bg="rgba(0, 255, 204, 0.15)" border="rgba(0, 255, 204, 0.45)" />
            </ul>
          </div>

          <div
            className="relative z-10 rounded-xl p-4 mb-4 border"
            style={{ borderColor: 'rgba(255, 0, 255, 0.35)', background: 'rgba(255, 0, 255, 0.06)' }}
          >
            <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-2 text-pink flex items-center gap-1.5">
              <Crown size={13} className="fill-current" /> Guest of Honor Receives
            </p>
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2 text-white text-sm">
                <Shirt size={14} className="text-pink shrink-0" /> VIP Br8k Room T-Shirt
              </li>
              <li className="flex items-center gap-2 text-white text-sm">
                <PartyPopper size={14} className="text-pink shrink-0" /> Snack or Drink
              </li>
            </ul>
          </div>

          <div
            className="relative z-10 rounded-xl p-4 mb-5 border"
            style={{
              borderColor: 'rgba(0, 255, 204, 0.35)',
              background: 'linear-gradient(90deg, rgba(0, 255, 204, 0.06), rgba(189, 0, 255, 0.08), rgba(0, 255, 204, 0.06))',
            }}
          >
            <div className="flex items-start gap-2">
              <Sparkles size={18} style={{ color: 'var(--neon-teal)' }} className="shrink-0 mt-0.5" />
              <p className="text-sm md:text-base">
                <span className="font-bold" style={{ color: 'var(--neon-teal)' }}>Upgrade choices:</span>{' '}
                <span className="text-white">swap your VR Adventure for </span>
                <span className="text-blue font-bold">VR Laser Tag</span>
                <span className="text-white">, </span>
                <span className="text-purple font-bold">VR Escape Room</span>
                <span className="text-white">, or </span>
                <span className="text-pink font-bold">Zero Latency</span>
                <span className="text-white">.</span>
              </p>
            </div>
          </div>

          <p className="relative z-10 text-center text-xs italic mb-5 flex items-center justify-center gap-1.5" style={{ color: 'var(--neon-teal)' }}>
            <Zap size={12} className="fill-current" /> Start the adventure. Level up the celebration.
          </p>

          <Button
            onClick={onBookNow}
            className="tier-book-btn btn-secondary w-full font-black py-6 text-base md:text-lg tracking-wider relative z-10 mt-auto"
            data-testid="rookie-mode-book-btn"
          >
            <PartyPopper size={18} className="mr-2" />
            BOOK NOW TO SEE PRICING
          </Button>
        </div>

        {/* PRO MODE */}
        <div
          data-testid="pro-mode-card"
          onMouseMove={setGlow}
          className="tier-card tier-card-popular relative bg-page border-2 rounded-2xl p-7 md:p-8 flex flex-col lg:scale-[1.03]"
          style={{
            borderColor: 'var(--neon-green)',
            '--tier-shadow-base': '0 0 30px rgba(0, 255, 65, 0.55), 0 0 60px rgba(0, 255, 65, 0.2)',
            '--tier-shadow-hover': '0 0 55px rgba(0, 255, 65, 0.95), 0 0 120px rgba(0, 255, 65, 0.45), 0 0 30px rgba(0, 255, 204, 0.35)',
            '--tier-accent': 'rgba(0, 255, 65, 0.65)',
            '--tier-solid': 'var(--neon-green)',
            '--tier-glow-ring': 'rgba(0, 255, 65, 0.75)',
            '--tier-glow-halo': 'rgba(0, 255, 65, 0.4)',
          }}
        >
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
            <span
              className="inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider whitespace-nowrap border-2"
              style={{
                background: 'linear-gradient(90deg, var(--neon-green) 0%, var(--neon-teal) 100%)',
                color: '#0a0e27',
                borderColor: 'var(--bg-page, #0a0e27)',
                boxShadow: '0 0 18px rgba(0, 255, 65, 0.85), 0 0 36px rgba(0, 255, 204, 0.45)',
              }}
            >
              <Star size={13} className="fill-current" />
              Take It To The Next Level
              <Star size={13} className="fill-current" />
            </span>
          </div>

          <div className="relative z-10 text-center mb-5 pb-5 border-b border-muted/50">
            <h3 className="text-3xl md:text-4xl font-black uppercase mb-3 text-primary" style={{ textShadow: '0 0 18px rgba(0, 255, 65, 0.6)' }}>
              Pro Mode
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <MetaChip icon={<Users size={13} className="text-primary" />} label="Up to 8 Guests" color="#00ff41" />
              <MetaChip icon={<Clock size={13} className="text-primary" />} label="~2.5 Hours" color="#00ff41" />
              <MetaChip icon={<Star size={13} className="text-primary" />} label="Ages 10+" color="#00ff41" />
            </div>
            <p className="text-secondary text-xs mt-3">Add up to 2 additional guests</p>
          </div>

          <p className="relative z-10 text-white/90 text-sm md:text-base leading-relaxed mb-6">
            Take your celebration to the next level. Built for groups craving more action — battle head-to-head in VR Laser Tag or team up to escape a thrilling VR Escape Room. A private party experience unlike anything else in Northwest Indiana.
          </p>

          <div className="relative z-10 mb-5">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-4 text-primary">
              Choose One Featured VR Adventure
            </p>
            <ul className="space-y-3 mb-5">
              <IncludeItem title="VR Laser Tag Experience" sub="30 minutes" accent="var(--neon-green)" bg="rgba(0, 255, 65, 0.15)" border="rgba(0, 255, 65, 0.45)" />
              <li className="text-center text-secondary text-xs font-black uppercase tracking-[0.3em]">— or —</li>
              <IncludeItem title="VR Escape Room Adventure" sub="Up to 60 minutes" accent="var(--neon-green)" bg="rgba(0, 255, 65, 0.15)" border="rgba(0, 255, 65, 0.45)" />
            </ul>

            <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-4 text-primary">
              Plus Everything Below
            </p>
            <ul className="space-y-3">
              <IncludeItem title="60 Minutes of Retro Gaming" sub="40,000+ games across 3 consoles" accent="var(--neon-green)" bg="rgba(0, 255, 65, 0.15)" border="rgba(0, 255, 65, 0.45)" />
              <IncludeItem title="Reserved Celebration Table Area" sub="Dedicated space for cake, gifts, and guests" accent="var(--neon-green)" bg="rgba(0, 255, 65, 0.15)" border="rgba(0, 255, 65, 0.45)" />
              <IncludeItem title="Lounge Area with TV & Nex Playground Access" sub="Chill zone for breaks between games" accent="var(--neon-green)" bg="rgba(0, 255, 65, 0.15)" border="rgba(0, 255, 65, 0.45)" />
            </ul>
          </div>

          <div
            className="relative z-10 rounded-xl p-4 mb-4 border"
            style={{ borderColor: 'rgba(255, 0, 255, 0.35)', background: 'rgba(255, 0, 255, 0.06)' }}
          >
            <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-2 text-pink flex items-center gap-1.5">
              <Crown size={13} className="fill-current" /> Guest of Honor Receives
            </p>
            <ul className="space-y-1.5">
              <li className="flex items-center gap-2 text-white text-sm">
                <Shirt size={14} className="text-pink shrink-0" /> VIP Br8k Room T-Shirt
              </li>
              <li className="flex items-center gap-2 text-white text-sm">
                <PartyPopper size={14} className="text-pink shrink-0" /> Snack or Drink
              </li>
            </ul>
          </div>

          <div
            className="relative z-10 rounded-xl p-4 mb-5 border"
            style={{
              borderColor: 'rgba(0, 255, 65, 0.4)',
              background: 'linear-gradient(90deg, rgba(0, 255, 65, 0.08), rgba(189, 0, 255, 0.08), rgba(0, 255, 65, 0.08))',
            }}
          >
            <div className="flex items-start gap-2">
              <Sparkles size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm md:text-base">
                <span className="text-primary font-bold">Upgrade choice:</span>{' '}
                <span className="text-white">swap your Featured VR Adventure for </span>
                <span className="text-pink font-bold">Zero Latency</span>
                <span className="text-white"> premium free-roam VR.</span>
              </p>
            </div>
          </div>

          <p className="relative z-10 text-center text-primary text-xs italic mb-5 flex items-center justify-center gap-1.5">
            <Zap size={12} className="fill-current" /> More action. More adventure. More memories.
          </p>

          <Button
            onClick={onBookNow}
            className="tier-book-btn btn-primary w-full font-black py-6 text-base md:text-lg tracking-wider relative z-10 mt-auto"
            data-testid="pro-mode-book-btn"
          >
            <PartyPopper size={18} className="mr-2" />
            BOOK NOW TO SEE PRICING
          </Button>
        </div>
      </div>
    </div>
  </section>
);
